import React, { useState, ReactNode, ReactElement } from 'react';
import {
  Box,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';

export interface StatusChip {
  label: string;
  color: 'error' | 'warning' | 'info' | 'success' | 'default' | 'primary' | 'secondary';
  icon?: ReactElement;
}

export interface ActionItem {
  id: string;
  label: string;
  icon: ReactElement;
  onClick: () => void;
  color?: string;
  tooltip?: string;
  conditional?: {
    condition: boolean;
    trueLabel: string;
    falseLabel: string;
    trueIcon?: ReactElement;
    falseIcon?: ReactElement;
  };
}

interface ItemCardProps<T> {
  item: T;
  rank: number;
  renderContent: (item: T) => ReactNode;
  statusChips?: StatusChip[];
  dimmed?: boolean;
  strikethrough?: boolean;
  actionMode: 'menu' | 'inline';
  actions: ActionItem[];
  onViewClick?: () => void;
}

function ItemCard<T>({
  item,
  rank,
  renderContent,
  statusChips = [],
  dimmed = false,
  strikethrough = false,
  actionMode,
  actions,
  onViewClick,
}: ItemCardProps<T>) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleActionClick = (action: ActionItem) => {
    action.onClick();
    if (actionMode === 'menu') {
      handleMenuClose();
    }
  };

  const renderAction = (action: ActionItem) => {
    const { conditional } = action;
    const label = conditional
      ? (conditional.condition ? conditional.trueLabel : conditional.falseLabel)
      : action.label;
    const icon = conditional?.trueIcon && conditional?.falseIcon
      ? (conditional.condition ? conditional.trueIcon : conditional.falseIcon)
      : action.icon;

    return { label, icon };
  };

  return (
    <>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          opacity: dimmed ? 0.4 : 1,
        }}
      >
        {/* Chips sit on their own row below the content: beside it, a side
            drawer's width squeezed the title down to a few characters. */}
        <Box sx={{ textDecoration: strikethrough ? 'line-through' : 'none' }}>
          {renderContent(item)}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
          {statusChips.map((chip, index) => (
            <Chip
              key={index}
              icon={chip.icon}
              label={chip.label}
              size="small"
              color={chip.color}
              sx={{ fontSize: '0.7rem', height: 18 }}
            />
          ))}
          <Chip
            label={`#${rank}`}
            size="small"
            color="secondary"
            variant="outlined"
            icon={<ViewIcon sx={{ fontSize: '0.75rem' }} />}
            onClick={onViewClick ? (e) => {
              e.stopPropagation();
              onViewClick();
            } : undefined}
            sx={{
              fontSize: '0.75rem',
              height: 20,
              cursor: onViewClick ? 'pointer' : 'default',
              '&:hover': onViewClick ? {
                backgroundColor: 'secondary.main',
                color: 'secondary.contrastText',
              } : {},
            }}
          />
        </Box>
      </Box>

      {actionMode === 'menu' ? (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', flexShrink: 0 }}>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{
              opacity: 0.6,
              '&:hover': { opacity: 1 },
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
          {actions.map((action) => {
            const { label, icon } = renderAction(action);
            return (
              <Tooltip key={action.id} title={action.tooltip || label}>
                <IconButton
                  size="small"
                  onClick={() => handleActionClick(action)}
                  sx={{
                    opacity: 0.7,
                    '&:hover': {
                      opacity: 1,
                      color: action.color || 'inherit',
                    }
                  }}
                >
                  {icon}
                </IconButton>
              </Tooltip>
            );
          })}
        </Box>
      )}

      {actionMode === 'menu' && (
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
        >
          {actions.map((action) => {
            const { label, icon } = renderAction(action);
            return (
              <MenuItem
                key={action.id}
                onClick={() => handleActionClick(action)}
              >
                <Box
                  component="span"
                  sx={{
                    mr: 1,
                    fontSize: 16,
                    color: action.color || 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {icon}
                </Box>
                {label}
              </MenuItem>
            );
          })}
        </Menu>
      )}
    </>
  );
}

export default ItemCard;
